import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Library as LibraryIcon, Plus, MapPin } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { getStoredLocation } from "@/lib/location";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { pageHead, jsonLd, SITE_URL } from "@/lib/seo";

type Library = {
  id: string;
  name: string;
  description: string | null;
  address: string | null;
  host_user_id: string;
  cover_url: string | null;
};

export const Route = createFileRoute("/libraries")({
  head: () => pageHead({
    path: "/libraries",
    title: "Community libraries near you — Book Swap",
    description: "Discover street libraries and shared shelves hosted by neighbors, see what's on them, and add your own to the map.",
    scripts: [
      jsonLd({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Community libraries",
        description: "Street libraries and shared shelves hosted by neighbors in Saudi Arabia.",
        url: `${SITE_URL}/libraries`,
        about: { "@type": "Place", name: "Community book libraries" },
      }),
    ],
  }),
  component: Libraries,
});

function Libraries() {
  const { user } = useAuth();
  const [items, setItems] = useState<Library[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", description: "" });
  const [saving, setSaving] = useState(false);

  async function load() {
    // Reads through a safe helper: exact street addresses stay visible to the host only.
    const { data } = await supabase.rpc("get_public_libraries" as never);
    const rows = ((data ?? []) as Library[]).sort((a, b) => (a.name > b.name ? 1 : -1));
    setItems(rows);
  }
  useEffect(() => { load(); }, []);

  async function create() {
    if (!user) return;
    setSaving(true);
    const coords = getStoredLocation();
    const { error } = await supabase.from("libraries").insert({
      host_user_id: user.id,
      name: form.name,
      address: form.address || null,
      description: form.description || null,
      lat: coords?.lat ?? null,
      lng: coords?.lng ?? null,
    });
    setSaving(false);
    if (error) { console.error(error); return toast.error("Could not create library."); }
    toast.success("Library created!");
    setForm({ name: "", address: "", description: "" });
    setOpen(false);
    load();
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <LibraryIcon className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold">Community Libraries</h1>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">Public drop-off spots where neighbors share books.</p>
          </div>
          {user && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90"><Plus className="me-1 h-4 w-4" /> Host a library</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Host a community library</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Marina Beach Library Box" /></div>
                  <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, area, city" /></div>
                  <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Hours, what to bring, etc." /></div>
                  <Button disabled={!form.name.trim() || saving} onClick={create} className="w-full bg-primary hover:bg-primary/90">
                    {saving ? "Creating…" : "Create"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {items.length === 0 ? (
          <div className="mt-12 rounded-2xl bg-card p-10 text-center shadow-[var(--shadow-card)]">
            <LibraryIcon className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">No community libraries yet. Be the first host!</p>
          </div>
        ) : (
          <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {items.map((l) => (
              <Link
                key={l.id}
                to="/libraries/$id"
                params={{ id: l.id }}
                className="rounded-2xl bg-card p-5 shadow-[var(--shadow-card)] transition hover:shadow-lg"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-2xl">🏛️</div>
                  <div className="min-w-0 flex-1">
                    <h2 className="truncate font-semibold">{l.name}</h2>
                    {l.address && (
                      <p className="mt-1 flex items-center gap-1 truncate text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" /> {l.address}
                      </p>
                    )}
                  </div>
                </div>
                {l.description && <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">{l.description}</p>}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
